'use client';

import React, { Suspense } from "react";
import { motion } from "framer-motion";
import { XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";

const falseSolutions = [
    { title: "Contratar mais secretárias", description: "Aumenta o custo fixo, mas não resolve a ineficiência." },
    { title: "Sistemas hospitalares", description: "Implementação complexa e cara (R$50.000+), inadequados para consultórios." },
    { title: "Chatbots genéricos", description: "Inseguros, sem validação médica e incapazes de seguir protocolos específicos." },
    { title: "Responder tudo sozinho", description: "Caminho direto para o burnout, comprometendo a qualidade do atendimento." },
];

const fsContainerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { staggerChildren: 0.2, duration: 0.5 },
    },
    exit: { opacity: 0, y: 30, transition: { staggerChildren: 0.1, duration: 0.3 } },
};

const cardVariants = (i: number) => ({
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            delay: i * 0.2,
            duration: 0.5,
        },
    },
    exit: {
      opacity: 0,
      y: 30,
      transition: {
          delay: i * 0.1,
          duration: 0.3,
      },
    },
});


type FalseSolutionsProps = {
  isBot: boolean;
};

export default function FalseSolutions({ isBot }: FalseSolutionsProps) {
    const Wrapper = isBot ? "div" : motion.div;

    return (
        <section id="false-solutions" className="relative py-10 sm:py-16 bg-secondary/50">
            <div
                className="container mx-auto px-4"
            >
                <div className="mx-auto max-w-2xl text-center">
                    <h2 className="font-headline text-3xl tracking-[-0.04em] leading-[1.1] text-black sm:text-4xl font-semibold">
                        As falsas soluções que só geram mais problemas
                    </h2>
                     <p className="mt-4 text-lg text-foreground/70 tracking-tight">
                        Muitos tentam resolver a sobrecarga de mensagens com abordagens que não funcionam a longo prazo.
                    </p>
                </div>
                <Suspense fallback={null}>
                  <Wrapper
                      variants={isBot ? undefined : fsContainerVariants}
                      initial={isBot ? undefined : "hidden"}
                      whileInView={isBot ? undefined : "visible"}
                      exit={isBot ? undefined : "exit"}
                      viewport={isBot ? undefined : { once: false, amount: 0.3 }}
                      className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2"
                  >
                      {falseSolutions.map((solution, index) => (
                          <Wrapper
                              key={index}
                              custom={index}
                              initial={isBot ? undefined : "hidden"}
                              whileInView={isBot ? undefined : "visible"}
                              exit={isBot ? undefined : "exit"}
                              viewport={isBot ? undefined : { once: false, amount: 0.5 }}
                              variants={isBot ? undefined : cardVariants(index)}
                          >
                              <Card className={cn("h-full bg-card border-border hover:shadow-md transition-shadow", index % 2 === 0 ? "message-bubble" : "message-bubble-right")}>
                                  <CardContent className="p-6">
                                      <div className="flex items-start gap-4">
                                          <XCircle className="mt-1 h-6 w-6 flex-shrink-0 text-destructive" />
                                          <div>
                                              <h3 className="font-semibold text-foreground tracking-tight">{solution.title}</h3>
                                              <p className="mt-1 text-sm text-foreground/70 tracking-tight">{solution.description}</p>
                                          </div>
                                      </div>
                                  </CardContent>
                              </Card>
                          </Wrapper>
                      ))}
                  </Wrapper>
                </Suspense>
            </div>
        </section>
    );
}
